import { CONSULTA_MOVIMIENTO_REDUCIDO } from './desplazamiento';

/**
 * El movimiento que el CÓDIGO dibuja, y su apagado (Historia 1.14 · NFR-10, 5.º criterio).
 *
 * ## Por qué hace falta, si `styles.css` ya apaga el movimiento
 *
 * El apagado general es una regla de CSS con `!important` sobre `animation-duration` y
 * `transition-duration`. Alcanza a todo lo que se anima con CSS —una utilidad de Tailwind, un
 * `@keyframes`, un `style` en línea— y **no alcanza a lo que anima JavaScript**: una animación de
 * la Web Animations API (`elemento.animate(…)`) lleva su duración en las opciones y no en una
 * propiedad de CSS, y `@angular/animations` corre sobre esa misma API. La contra-auditoría del
 * 2026-09-28 lo midió: una entrada de 200 ms por `element.animate` en `app-toast`, sin apagado,
 * pasó el gate en 8 archivos y 98 tests.
 *
 * Por eso ningún componente escribe `.animate(` a mano: pide el movimiento acá, que mira la
 * preferencia en cada llamada, y el gate (`src/gate/foco-y-movimiento.spec.ts`) se pone rojo si
 * aparece fuera de este archivo. Lo mismo el motor de `@angular/animations`: lo decide
 * {@link motorDeAnimaciones} al arrancar.
 */

/** Si quien usa la consola pidió menos movimiento. Sin `matchMedia`, ante la duda, sí. */
export function prefiereMenosMovimiento(): boolean {
  if (typeof globalThis.matchMedia !== 'function') return true;
  return globalThis.matchMedia(CONSULTA_MOVIMIENTO_REDUCIDO).matches;
}

/**
 * `elemento.animate(…)`, con el apagado: para quien pidió menos movimiento la duración es cero y
 * el estado final queda puesto (`fill: 'forwards'`) — lo que queda es el cambio de estado, no el
 * recorrido, que es lo que el 4.º criterio pide para el CSS.
 *
 * Se consulta en cada llamada: la preferencia se puede cambiar con la consola abierta.
 */
export function animar(
  elemento: Element,
  cuadros: Keyframe[] | PropertyIndexedKeyframes,
  opciones: KeyframeAnimationOptions,
): Animation {
  if (!prefiereMenosMovimiento()) return elemento.animate(cuadros, opciones);
  return elemento.animate(cuadros, { ...opciones, duration: 0, delay: 0, fill: 'forwards' });
}

/**
 * El motor de `@angular/animations` que se provee al arrancar: `'noop'` para quien pidió menos
 * movimiento, que deja cada `trigger` en su estado final sin recorrido —de la app y de las
 * dependencias, como los toasts de `ngx-toastr`—.
 *
 * Se decide UNA vez, al arrancar: el proveedor no se puede cambiar en caliente. Quien cambie la
 * preferencia con la consola abierta ve el cambio al recargar; el CSS y {@link animar} lo ven al
 * instante.
 */
export function motorDeAnimaciones(): 'animations' | 'noop' {
  return prefiereMenosMovimiento() ? 'noop' : 'animations';
}
